import { ClipboardList } from 'lucide-react'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const completedIcon = (
  <ClipboardList className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

export function TicketsListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count =
    typeof state.result?.count === 'number' ? state.result.count : null
  const tickets: Array<Record<string, unknown>> = Array.isArray(
    state.result?.tickets
  )
    ? state.result.tickets
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Listing tickets...'
        completedLabel={
          count !== null
            ? `${plural(count, 'ticket')} loaded`
            : 'Tickets loaded'
        }
        completedDetail={pickString(state.args?.status, state.args?.assignee)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No tickets found'
          items={tickets.map((ticket) => ({
            id: pickString(ticket.key, ticket.identifier, ticket.id),
            title:
              pickString(ticket.title, ticket.summary) ?? 'Untitled ticket',
            subtitle: pickString(ticket.assigneeName, ticket.assignee),
            meta: [ticket.status, ticket.provider, ticket.priority],
            href: pickString(ticket.url, ticket.webUrl),
          }))}
        />
      ) : null}
    </>
  )
}

export function TicketsGetToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const ticket = state.result?.ticket ?? state.result
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading ticket...'
        completedLabel='Ticket loaded'
        loadingDetail={pickString(state.args?.ticketId)}
        completedDetail={pickString(ticket?.title, state.args?.ticketId)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) && ticket ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(
            part,
            `ticket-${pickString(ticket.key, ticket.identifier, ticket.id, state.args?.ticketId) ?? 'detail'}`
          )}
          payload={{
            entityType: 'ticket',
            toolName: 'ticketsGet',
            title:
              pickString(ticket.title, ticket.summary) ?? 'Untitled ticket',
            subtitle: pickString(ticket.key, ticket.identifier, ticket.id),
            description: pickString(ticket.description, ticket.body),
            href: pickString(ticket.url, ticket.webUrl),
            fields: [
              { label: 'Status', value: formatToolValue(ticket.status) },
              { label: 'Provider', value: formatToolValue(ticket.provider) },
              { label: 'Priority', value: formatToolValue(ticket.priority) },
              {
                label: 'Assignee',
                value: pickString(ticket.assigneeName, ticket.assignee),
              },
              {
                label: 'Labels',
                value: Array.isArray(ticket.labels)
                  ? ticket.labels.join(', ')
                  : null,
              },
            ],
          }}
        />
      ) : null}
    </>
  )
}

export function TicketsCreateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Creating ticket...'
      completedLabel='Ticket created'
      loadingDetail={pickString(state.args?.title)}
      completedDetail={pickString(state.args?.title, state.result?.title)}
      completedIcon={completedIcon}
    />
  )
}

export function TicketsUpdateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Updating ticket...'
      completedLabel='Ticket updated'
      completedDetail={pickString(state.args?.ticketId)}
      completedIcon={completedIcon}
    />
  )
}

export function TicketsCloseToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Closing ticket...'
      completedLabel='Ticket closed'
      completedDetail={pickString(state.args?.ticketId)}
      completedIcon={completedIcon}
    />
  )
}

export function TicketsDeleteToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Deleting ticket...'
      completedLabel='Ticket deleted'
      completedDetail={pickString(state.args?.ticketId)}
      completedIcon={completedIcon}
    />
  )
}
